import React, { ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import NoData from '@rio-cloud/rio-uikit/NoData';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import TableCol from '@rio-cloud/rio-uikit/TableCol';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import isEmpty from 'lodash/fp/isEmpty';

import Pagination from '@/features/common/components/maintenance-table/Pagination';
import { TableHeader } from '@/features/common/components/maintenance-table/TableHeader';
import { TableRow } from '@/features/common/components/maintenance-table/TableRow';
import { ColumnConfig, RowStatusMapper } from '@/features/common/components/maintenance-table/types';
import { CommonSortBy } from '@/features/common/hooks/useCommonQueryParams';
import { PaginationHandling } from '@/features/common/hooks/usePagination';
import { SortHandling } from '@/features/common/hooks/useSorting';
import { Brake, PageMetadata } from '@/generated/GeneratedApiTypes';

export const renderNullable = (value?: any, messageKey?: string) => {
    const intl = useIntl();
    return value ? (
        <span>{value}</span>
    ) : (
        <NoData
            text={intl.formatMessage({ id: messageKey ?? 'intl-msg:vehicle-diagnostics.maintenance.table.noData' })}
        />
    );
};

export const accountRenderer = ({ account }: Brake) => {
    return renderNullable(account.name);
};

export const vehicleRenderer = ({ asset }: Brake) => {
    return renderNullable(asset.name);
};

export const vinRenderer = ({ asset }: Brake) => {
    return renderNullable(asset.vin);
};

export const workshopRenderer = ({ workshop }: Brake) => {
    return renderNullable(workshop?.name);
};

export const DEFAULT_COLUMNS_CONFIG: ColumnConfig[] = [
    {
        name: 'vehicle',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.vehicle',
        hidden: false,
        sortable: true,
        renderer: vehicleRenderer,
    },
    {
        name: 'vin',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.vin',
        hidden: false,
        sortable: true,
        renderer: vinRenderer,
    },
    {
        name: 'customer',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.customer',
        hidden: false,
        sortable: true,
        renderer: accountRenderer,
    },
    {
        name: 'workshop',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.workshop',
        hidden: false,
        sortable: true,
        renderer: workshopRenderer,
    },
];

interface MaintenanceTableProps<S> {
    columnConfig: ColumnConfig[];
    enableRowSelection?: boolean;
    error?: FetchBaseQueryError;
    isError: boolean;
    isFetching: boolean;
    isLoading: boolean;
    pageMetadata?: PageMetadata;
    pageSizeOptions?: number[];
    pagination: PaginationHandling;
    sorting: SortHandling<S>;
    rows?: any[];
    leftToolbar?: React.ReactNode;
    rightToolbar?: React.ReactNode;
    rowStatusMapper?: RowStatusMapper;
    rowDetailRenderer: (data: any) => ReactNode;
}

export const MaintenanceTable = <S = CommonSortBy,>({
    columnConfig,
    enableRowSelection = false,
    error,
    isError,
    isFetching,
    isLoading,
    pageMetadata,
    pageSizeOptions,
    pagination,
    sorting,
    rows,
    leftToolbar,
    rightToolbar,
    rowStatusMapper = (_) => '',
    rowDetailRenderer,
}: MaintenanceTableProps<S>) => {
    const handleSortChange = useCallback(
        (event: React.MouseEvent<HTMLTableCellElement>) => {
            const newSortBy = event.currentTarget.dataset.sortby;
            if (newSortBy) {
                sorting.setSortBy(newSortBy as S);
            }
            if (newSortBy === sorting.sortBy) {
                sorting.toggleDirection();
            } else {
                sorting.setSortDirection('ASC');
            }
        },
        [sorting.sortBy],
    );
    return (
        <div className={'maintenance-table-container'}>
            <TableToolbar>
                <div className="table-toolbar-container">
                    <div className="table-toolbar-group-left">{leftToolbar}</div>
                    <div className="table-toolbar-group-right">{rightToolbar}</div>
                </div>
            </TableToolbar>
            <div className="table-responsive">
                <table className="maintenance-table table table-hover table-bordered">
                    <colgroup>
                        {columnConfig.map((column) => (
                            <TableCol key={column.name} />
                        ))}
                    </colgroup>
                    <TableHeader
                        columns={columnConfig}
                        sortBy={sorting.sortBy}
                        sortDirection={sorting.sortDirection}
                        handleSortChange={handleSortChange}
                    />
                    <tbody>
                        {(isLoading || isFetching) && (
                            <tr>
                                <td colSpan={columnConfig.length}>
                                    <Spinner text={'Loading...'} />
                                </td>
                            </tr>
                        )}
                        {!isLoading && !isFetching && isError && (
                            <tr>
                                <td colSpan={columnConfig.length}>
                                    <NotFoundState
                                        outerClassName="border-none"
                                        headline={error?.status === 404 ? 'No data found' : 'Something went wrong'}
                                        message={
                                            error?.status === 404 ? 'Please refine your search' : 'Something went wrong'
                                        }
                                    />
                                </td>
                            </tr>
                        )}
                        {!isLoading && !isFetching && !isError && !isEmpty(rows) && (
                            <TableRow
                                rowStatusMapper={rowStatusMapper}
                                rows={rows}
                                columns={columnConfig}
                                rowDetailRenderer={rowDetailRenderer}
                            />
                        )}
                        {!isLoading && !isFetching && !isError && isEmpty(rows) && (
                            <tr>
                                <td colSpan={columnConfig.length}>
                                    <NotFoundState
                                        outerClassName="border-none"
                                        headline="Nothing found"
                                        message="Please refine your search"
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <Pagination
                pageMetadata={isError ? undefined : pageMetadata}
                onPageChange={pagination.setPageNumber}
                onPageSizeChange={pagination.setPageSize}
                pageSizeOptions={pageSizeOptions}
            />
        </div>
    );
};
